import { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, QrCode, Download } from "lucide-react";
import { QRCodeCanvas } from "qrcode.react";
import api from "@/lib/api";

const blank = { serial: "", model: "", product_slug: "", client_name: "", client_city: "", install_date: "", warranty_until: "", notes: "", maintenance_history: [] };

export default function EquipmentAdmin() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(blank);
  const [qr, setQr] = useState(null);
  const qrRef = useRef();

  const load = () => api.get("/admin/equipment").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const openNew = () => { setForm(blank); setEditing(false); setOpen(true); };
  const openEdit = (e) => { setForm({ ...e, maintenance_history: e.maintenance_history || [] }); setEditing(true); setOpen(true); };

  const maintText = (arr) => (arr || []).map((m) => `${m.date} | ${m.description}`).join("\n");
  const parseMaint = (txt) => txt.split("\n").map((l) => l.trim()).filter(Boolean).map((l) => {
    const [date, ...rest] = l.split("|");
    return { date: (date || "").trim(), description: rest.join("|").trim() };
  });

  const save = async () => {
    if (!form.serial) { toast.error("Informe o número de série."); return; }
    const payload = { ...form, maintenance_history: typeof form.maintenance_history === "string" ? parseMaint(form.maintenance_history) : form.maintenance_history };
    try {
      if (editing) await api.put(`/admin/equipment/${encodeURIComponent(form.serial)}`, payload);
      else await api.post("/admin/equipment", payload);
      toast.success("Equipamento salvo"); setOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao salvar"); }
  };
  const del = async (serial) => { if (!window.confirm("Excluir equipamento?")) return; await api.delete(`/admin/equipment/${encodeURIComponent(serial)}`); load(); toast.success("Excluído"); };

  const downloadQr = () => {
    const canvas = qrRef.current?.querySelector("canvas");
    if (!canvas) return;
    const a = document.createElement("a");
    a.href = canvas.toDataURL("image/png");
    a.download = `qr-${qr}.png`;
    a.click();
  };

  const qrUrl = qr ? `${window.location.origin}/equipamento/${encodeURIComponent(qr)}` : "";

  return (
    <div>
      <div className="flex items-center justify-between">
        <div><h1 className="font-heading text-2xl font-bold text-white">Pós-venda</h1><p className="mt-1 text-sm text-[#A1A1AA]">Cadastro de equipamentos por nº de série, garantia, histórico e QR Code.</p></div>
        <Button data-testid="equipment-new-button" onClick={openNew} className="rounded-none bg-[#FF5500] font-bold hover:bg-[#CC4400]"><Plus className="mr-1 h-4 w-4" /> Novo</Button>
      </div>

      <div className="mt-5 overflow-x-auto border border-[#27272A] bg-[#151618]">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-[#27272A] text-left text-[0.7rem] uppercase tracking-wider text-[#A1A1AA]">
            <th className="px-4 py-3">Nº Série</th><th className="px-4 py-3">Modelo</th><th className="px-4 py-3">Cliente</th><th className="px-4 py-3">Garantia</th><th className="px-4 py-3"></th></tr></thead>
          <tbody>
            {items.map((e) => (
              <tr key={e.serial} data-testid={`equipment-row-${e.serial}`} className="border-b border-[#27272A] last:border-0 hover:bg-[#1E1F22]">
                <td className="px-4 py-3 font-bold text-white">{e.serial}</td>
                <td className="px-4 py-3 text-[#A1A1AA]">{e.model || "—"}</td>
                <td className="px-4 py-3 text-[#A1A1AA]">{e.client_name || "—"}</td>
                <td className="px-4 py-3 text-[#A1A1AA]">{e.warranty_until || "—"}</td>
                <td className="px-4 py-3"><div className="flex gap-3">
                  <button onClick={() => setQr(e.serial)} data-testid={`equipment-qr-${e.serial}`}><QrCode className="h-4 w-4 text-[#007AFF] hover:text-white" /></button>
                  <button onClick={() => openEdit(e)} data-testid={`equipment-edit-${e.serial}`}><Pencil className="h-4 w-4 text-[#A1A1AA] hover:text-white" /></button>
                  <button onClick={() => del(e.serial)} data-testid={`equipment-delete-${e.serial}`}><Trash2 className="h-4 w-4 text-[#71717A] hover:text-red-400" /></button>
                </div></td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={5} className="px-4 py-8 text-center text-[#A1A1AA]">Nenhum equipamento cadastrado.</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto rounded-none border-[#27272A] bg-[#151618] text-white" data-testid="equipment-dialog">
          <DialogHeader><DialogTitle className="font-heading">{editing ? "Editar equipamento" : "Novo equipamento"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="label-eyebrow">Nº de série *</Label><Input data-testid="equipment-form-serial" disabled={editing} value={form.serial} onChange={(e) => set("serial", e.target.value)} className="mt-1 rounded-none border-[#27272A] bg-[#0A0A0B]" /></div>
              <div><Label className="label-eyebrow">Modelo</Label><Input data-testid="equipment-form-model" value={form.model} onChange={(e) => set("model", e.target.value)} className="mt-1 rounded-none border-[#27272A] bg-[#0A0A0B]" /></div>
              <div><Label className="label-eyebrow">Cliente</Label><Input data-testid="equipment-form-client" value={form.client_name} onChange={(e) => set("client_name", e.target.value)} className="mt-1 rounded-none border-[#27272A] bg-[#0A0A0B]" /></div>
              <div><Label className="label-eyebrow">Cidade</Label><Input data-testid="equipment-form-city" value={form.client_city} onChange={(e) => set("client_city", e.target.value)} className="mt-1 rounded-none border-[#27272A] bg-[#0A0A0B]" /></div>
              <div><Label className="label-eyebrow">Data de instalação</Label><Input data-testid="equipment-form-install" value={form.install_date} onChange={(e) => set("install_date", e.target.value)} placeholder="DD/MM/AAAA" className="mt-1 rounded-none border-[#27272A] bg-[#0A0A0B]" /></div>
              <div><Label className="label-eyebrow">Garantia até</Label><Input data-testid="equipment-form-warranty" value={form.warranty_until} onChange={(e) => set("warranty_until", e.target.value)} placeholder="DD/MM/AAAA" className="mt-1 rounded-none border-[#27272A] bg-[#0A0A0B]" /></div>
            </div>
            <div><Label className="label-eyebrow">Observações</Label><Textarea data-testid="equipment-form-notes" value={form.notes} onChange={(e) => set("notes", e.target.value)} className="mt-1 rounded-none border-[#27272A] bg-[#0A0A0B]" /></div>
            <div><Label className="label-eyebrow">Histórico de manutenção (uma por linha: data | descrição)</Label>
              <Textarea data-testid="equipment-form-maint" rows={4} value={typeof form.maintenance_history === "string" ? form.maintenance_history : maintText(form.maintenance_history)} onChange={(e) => set("maintenance_history", e.target.value)} placeholder="10/06/2026 | Troca de filtro secador" className="mt-1 rounded-none border-[#27272A] bg-[#0A0A0B]" /></div>
            <Button data-testid="equipment-form-save" onClick={save} className="w-full rounded-none bg-[#FF5500] font-bold hover:bg-[#CC4400]">Salvar</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!qr} onOpenChange={(o) => !o && setQr(null)}>
        <DialogContent className="max-w-sm rounded-none border-[#27272A] bg-[#151618] text-white" data-testid="equipment-qr-dialog">
          <DialogHeader><DialogTitle className="font-heading">QR Code — {qr}</DialogTitle></DialogHeader>
          <div ref={qrRef} className="flex flex-col items-center gap-4 py-4">
            {qrUrl && <div className="bg-white p-4"><QRCodeCanvas value={qrUrl} size={200} level="M" /></div>}
            <p className="break-all text-center text-xs text-[#A1A1AA]">{qrUrl}</p>
            <Button data-testid="equipment-qr-download" onClick={downloadQr} className="rounded-none bg-[#FF5500] font-bold hover:bg-[#CC4400]"><Download className="mr-2 h-4 w-4" /> Baixar QR (PNG)</Button>
            <p className="text-center text-xs text-[#A1A1AA]">Cole o QR na etiqueta do equipamento. Ao escanear, o cliente vê garantia, histórico e pode pedir assistência.</p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
